/**
 * UnifiedExerciseCounter - Shared component for exercise detection and counting
 *
 * Uses the Strategy pattern to support multiple exercise types with unified UI.
 * Strategies handle exercise-specific detection loops, visualizations, and UI elements.
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { useAudioBeep, AUDIO_PRESETS } from './hooks/useAudioBeep.js';
import { useCamera } from './hooks/useCamera.js';
import { useDetector } from './hooks/useDetector.js';
import { useMediaRecorder } from './hooks/useMediaRecorder.js';
import { createStrategy } from './strategies/index.js';

/**
 * @typedef {Object} UnifiedExerciseCounterProps
 * @property {'skipping'|'pickleball'} exerciseType - Type of exercise
 * @property {number} requiredReps - Goal number of reps
 * @property {function} onComplete - Called when goal is reached
 * @property {function} onRepComplete - Called on each rep
 * @property {function} onProgressSave - Called to save progress
 * @property {function} onSessionEnd - Called when session ends
 * @property {string} modelPath - Path to YOLO model (for pickleball)
 * @property {'tfjs'|'onnx'} engine - Inference engine for YOLO (tfjs or onnx)
 * @property {boolean} disableXGBoost - Disable XGBoost prediction (for performance debugging)
 * @property {Object} labels - UI label overrides
 * @property {string} railsEnv - Rails environment (development/production)
 * @property {boolean} showPreStartChecklist - Show checklist before starting
 * @property {boolean} showReportButton - Show report inaccuracy button
 * @property {boolean} showRecordingControls - Show save movement JSON button
 * @property {string} streamingUploadUrl - URL to upload video recordings
 * @property {boolean} showMaxFPSOption - Show Max FPS benchmark option
 */

const UnifiedExerciseCounter = ({
  exerciseType = 'skipping',
  requiredReps,
  initialReps = 0,
  onComplete,
  onRepComplete,
  onProgressSave,
  onSessionEnd,
  modelPath,
  engine = 'tfjs-webgl',  // 'tfjs-webgl', 'tfjs-webgpu', or 'onnx-webgpu' for YOLO inference
  disableXGBoost = false,  // Disable XGBoost prediction (for performance debugging)
  labels = {},
  railsEnv = 'development',
  showPreStartChecklist = false,
  showReportButton = false,
  showRecordingControls = false,
  streamingUploadUrl = null,
  showMaxFPSOption = false
}) => {
  // Validate required props
  if (!requiredReps) {
    throw new Error('UnifiedExerciseCounter: requiredReps prop is required');
  }

  // Create strategy instance
  const strategy = useMemo(() => createStrategy(exerciseType), [exerciseType]);

  // Get labels from strategy, with overrides
  const strategyLabels = strategy.getLabels();
  const defaultLabels = {
    title: strategyLabels.title,
    start_exercise: 'Start Exercise',
    start_with_streaming: 'Start With Streaming',
    stop_exercise: 'Stop Exercise',
    resume_exercise: 'Resume Exercise',
    resume_with_streaming: 'Resume With Streaming',
    finish_exercise: 'Finish Exercise',
    test_sound: 'Test Sound',
    report_inaccuracy: 'Report Inaccurate Tracking',
    reps_completed: strategyLabels.repNoun,
    goal: 'Goal',
    loading_model: strategyLabels.loadingModel,
    camera_tip: strategyLabels.cameraTip,
    goal_achieved: 'Goal achieved!',
    did_you_hear_beep: 'Did you hear the beep sound?',
    yes: 'Yes',
    no: 'No',
    check_silent_mode: 'Please turn off silent mode and increase your device volume, then try again.',
    ok: 'Ok',
    start: 'Start',
    camera_sees_whole_body: 'The device camera will see my whole body',
    beep_on_correct_rep: strategyLabels.beepMessage,
    ball: strategyLabels.ball || 'Ball',
    paddle: strategyLabels.paddle || 'Paddle',
    detected: strategyLabels.detected || 'Detected',
    not_detected: strategyLabels.notDetected || 'Not detected'
  };

  const l = { ...defaultLabels, ...labels };

  // State
  const [repCount, setRepCount] = useState(initialReps);
  const [isActive, setIsActive] = useState(false);
  const [isStopped, setIsStopped] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [movementLog, setMovementLog] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [detectionStatus, setDetectionStatus] = useState(strategy.getInitialStatus());
  const [debugState, setDebugState] = useState(strategy.getInitialDebugState());

  // Dialog states
  const [showSoundTestDialog, setShowSoundTestDialog] = useState(false);
  const [showSilentModeWarning, setShowSilentModeWarning] = useState(false);
  const [showPreStartDialog, setShowPreStartDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportedReps, setReportedReps] = useState('');
  const [cameraCheckboxChecked, setCameraCheckboxChecked] = useState(false);
  const [beepCheckboxChecked, setBeepCheckboxChecked] = useState(false);

  // Recording state
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // Additional models loading state (e.g., pose detector for soccer)
  const [additionalModelsReady, setAdditionalModelsReady] = useState(false);

  // Progress checkpoint saving state
  const [isSavingProgress, setIsSavingProgress] = useState(false);
  const MAX_RECORDING_FRAMES = strategy.getMaxRecordingFrames();

  // Refs
  const isActiveRef = useRef(false);
  const lastRepCountRef = useRef(0);
  const lastSavedReps = useRef(initialReps);
  const sessionStartTimeRef = useRef(null);
  const movementLogRef = useRef([]);
  const frameCounterRef = useRef(0);
  const cleanupDetectionRef = useRef(null);
  const recordedVideoDimensionsRef = useRef(null); // Capture video dimensions at session start

  // Hooks
  const audioPresetKey = strategy.constructor.audioPreset || 'skipping';
  const audioPreset = AUDIO_PRESETS[audioPresetKey] || AUDIO_PRESETS.skipping;
  const { playBeep, playTestBeep, initAudioContext, cleanup: cleanupAudio } = useAudioBeep(audioPreset);
  const {
    videoRef,
    canvasRef,
    isReady: cameraReady,
    isLoading: cameraLoading,
    error: cameraError,
    startCamera,
    stopCamera,
    syncCanvasSize,
    clearCanvas,
    getStream
  } = useCamera();

  // Media recorder for video streaming (uploads chunks every second)
  const {
    isRecording: isVideoRecording,
    recordedBlob,
    duration: recordingDuration,
    uploadedChunks,
    sessionId: recordingSessionId,
    startRecording,
    stopRecording,
    uploadRecording,
    clearRecording,
    cleanup: cleanupRecording
  } = useMediaRecorder();

  // Video upload state
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [videoUploadStatus, setVideoUploadStatus] = useState(null);
  const [videoUploadMessage, setVideoUploadMessage] = useState(null);

  // Streaming session state
  const [isStreamingSession, setIsStreamingSession] = useState(false);
  const [showStartDropdown, setShowStartDropdown] = useState(false);
  const [showResumeDropdown, setShowResumeDropdown] = useState(false);
  const isStreamingSessionRef = useRef(false); // Ref for immediate access in callbacks

  // Max FPS session state (for benchmarking)
  const [isMaxFPSSession, setIsMaxFPSSession] = useState(false);
  const isMaxFPSSessionRef = useRef(false); // Ref for immediate access in callbacks

  const {
    isLoading: detectorLoading,
    isReady: detectorReady,
    error: detectorError,
    detect,
    updateCounter,
    getCounterState,
    resetCounter,
    getModelVersions,
    switchEngine,
    currentEngine,
    isSwitchingEngine
  } = useDetector({ exerciseType, modelPath, railsEnv, engine, disableXGBoost });

  // Initialize strategy with refs
  useEffect(() => {
    strategy.initialize({
      videoRef,
      canvasRef,
      detect,
      updateCounter,
      getCounterState,
      syncCanvasSize,
      clearCanvas
    });
  }, [strategy, videoRef, canvasRef, detect, updateCounter, getCounterState, syncCanvasSize, clearCanvas]);

  // Update strategy with current engine for display
  useEffect(() => {
    if (strategy.setCurrentEngine) {
      strategy.setCurrentEngine(currentEngine);
    }
  }, [strategy, currentEngine]);

  // Initialize additional models (e.g., pose detector for soccer) when detector is ready
  useEffect(() => {
    if (detectorReady && !additionalModelsReady) {
      const initAdditional = async () => {
        if (strategy.initializeAdditionalModels) {
          await strategy.initializeAdditionalModels(railsEnv);
        }
        setAdditionalModelsReady(strategy.isAdditionalModelsReady?.() ?? true);
      };
      initAdditional();
    }
  }, [detectorReady, additionalModelsReady, strategy, railsEnv]);

  // Combined loading/error state - wait for both detector and additional models
  const isLoading = detectorLoading || (detectorReady && !additionalModelsReady);
  const error = detectorError || cameraError;

  // Update debug state when detector is ready (for pickleball YOLO + XGBoost status)
  useEffect(() => {
    if (strategy.renderDebugPanel) {
      const updateStatus = () => {
        const counterState = getCounterState();
        const modelVersions = getModelVersions();
        setDebugState({
          // XGBoost ML model status
          predictions: counterState?.predictionsThisSession || 0,
          lastProb: counterState?.lastProbability || 0,
          bufferSize: counterState?.bufferSize || 0,
          isModelLoaded: counterState?.isModelLoaded || false,
          isLoading: counterState?.isLoading || false,
          loadError: counterState?.loadError || null,
          // YOLO detector status
          yoloReady: detectorReady,
          yoloLoading: detectorLoading,
          yoloError: detectorError,
          // Model versions
          modelVersions
        });
      };

      updateStatus();
      const timer = setTimeout(updateStatus, 500);
      return () => clearTimeout(timer);
    }
  }, [detectorReady, detectorLoading, detectorError, getCounterState, getModelVersions, strategy]);

  // Save progress checkpoint at multiples of 50 reps
  useEffect(() => {
    if (repCount > 0 && repCount % 50 === 0 && repCount > lastSavedReps.current) {
      lastSavedReps.current = repCount;
      if (onProgressSave) {
        setIsSavingProgress(true);
        Promise.resolve(onProgressSave({ reps: repCount })).finally(() => {
          setIsSavingProgress(false);
        });
      }
    }
  }, [repCount, onProgressSave]);

  // Handle rep detected - called by strategy or fake button
  // Increments by count (default 1) - the caller is responsible for detecting when a rep happens
  const handleRepDetected = (count = 1) => {
    setRepCount(prev => {
      const newCount = prev + count;
      // Sync lastRepCountRef so canvas displays the same count
      lastRepCountRef.current = newCount;
      if (onRepComplete) {
        onRepComplete(newCount);
      }
      return newCount;
    });
    playBeep();
    // Note: Don't auto-complete when goal is reached - let user click "Finish Exercise" when ready
  };

  // Handle start camera and detection
  const handleStartCamera = async () => {
    const success = await startCamera();
    if (!success) return;

    sessionStartTimeRef.current = Date.now();
    movementLogRef.current = strategy.initializeMovementLog(MAX_RECORDING_FRAMES);
    frameCounterRef.current = 0;

    // Capture video dimensions NOW when camera is ready
    // This is critical because MoveNet returns keypoints in video coordinate space
    // and we need the ACTUAL dimensions for correct normalization during replay
    const video = videoRef.current;
    if (video && video.videoWidth && video.videoHeight) {
      recordedVideoDimensionsRef.current = {
        width: video.videoWidth,
        height: video.videoHeight
      };
      console.log('Captured video dimensions at session start:', recordedVideoDimensionsRef.current);
    } else {
      console.warn('Video dimensions not available at session start, will try again on first frame');
      recordedVideoDimensionsRef.current = null;
    }

    // Start video recording if streaming session is enabled (use ref for immediate value)
    if (isStreamingSessionRef.current && streamingUploadUrl) {
      const stream = getStream();
      if (stream) {
        clearRecording();
        setVideoUploadStatus(null);
        setVideoUploadMessage(null);
        // Pass upload config for chunk-based streaming uploads
        startRecording(stream, {
          uploadUrl: streamingUploadUrl,
          exerciseType
        });
        console.log('[UnifiedExerciseCounter] Streaming recording started');
      } else {
        console.warn('[UnifiedExerciseCounter] No stream available for recording');
      }
    }

    isActiveRef.current = true;
    setIsActive(true);

    // Start detection loop via strategy
    cleanupDetectionRef.current = strategy.startDetectionLoop({
      onRepDetected: handleRepDetected,
      onStatusUpdate: setDetectionStatus,
      // Merge counter state updates with existing YOLO detector state
      onDebugUpdate: (counterState) => setDebugState(prev => ({ ...prev, ...counterState })),
      onFrameRecorded: showRecordingControls ? (frameData, timestamp, frameIndex) => {
        // Frame recording is handled by strategy
      } : null,
      isActiveRef,
      sessionStartTimeRef,
      frameCounterRef,
      lastRepCountRef,
      movementLogRef,
      // Pass ref so detection loop can capture dimensions on first frame if needed
      recordedVideoDimensionsRef,
      // Max FPS mode for benchmarking (use ref for immediate value)
      maxFPS: isMaxFPSSessionRef.current
    });
  };

  // Handle stop
  const handleStop = () => {
    isActiveRef.current = false;
    setIsActive(false);
    setIsStopped(true);

    // Save progress checkpoint when stopping
    if (onProgressSave && repCount > lastSavedReps.current) {
      lastSavedReps.current = repCount;
      setIsSavingProgress(true);
      Promise.resolve(onProgressSave({ reps: repCount })).finally(() => {
        setIsSavingProgress(false);
      });
    }

    if (cleanupDetectionRef.current) {
      cleanupDetectionRef.current();
      cleanupDetectionRef.current = null;
    }

    // Stop video recording if it was active (use ref for immediate value)
    if (isStreamingSessionRef.current && isVideoRecording) {
      stopRecording();
      console.log('[UnifiedExerciseCounter] Streaming recording stopped');
    }

    stopCamera();
    setMovementLog([...strategy.sliceMovementLog(movementLogRef.current, frameCounterRef.current)]);

    if (onSessionEnd) {
      onSessionEnd(repCount);
    }
  };

  // Show upload status when streaming recording completes
  // Chunks are uploaded during recording, so we just show completion status
  useEffect(() => {
    if (isStreamingSession && streamingUploadUrl && recordedBlob && !isVideoRecording) {
      // Recording stopped, all chunks should be uploaded
      setVideoUploadStatus('success');
      setVideoUploadMessage(`Streaming complete! Session: ${recordingSessionId}, ${uploadedChunks} chunks uploaded`);
      console.log(`[UnifiedExerciseCounter] Streaming complete. Session: ${recordingSessionId}, Chunks: ${uploadedChunks}`);

      // Reset streaming session state
      isStreamingSessionRef.current = false;
      setIsStreamingSession(false);
    }
  }, [recordedBlob, isVideoRecording, isStreamingSession, streamingUploadUrl, recordingSessionId, uploadedChunks]);

  // Handle resume
  const handleResume = () => {
    setIsStopped(false);
    handleStartCamera();
  };

  // Handle resume button click (with dropdown support)
  const handleResumeClick = ({ withStreaming = false, withMaxFPS = false } = {}) => {
    // Clear previous recording state to prevent useEffect from resetting isStreamingSession
    if (withStreaming) {
      clearRecording();
    }
    // Set both ref (for immediate access) and state (for UI)
    isStreamingSessionRef.current = withStreaming;
    setIsStreamingSession(withStreaming);
    isMaxFPSSessionRef.current = withMaxFPS;
    setIsMaxFPSSession(withMaxFPS);
    setShowResumeDropdown(false);
    handleResume();
  };

  // Handle start button click (without streaming)
  const handleStartClick = ({ withStreaming = false, withMaxFPS = false } = {}) => {
    // Set both ref (for immediate access) and state (for UI)
    isStreamingSessionRef.current = withStreaming;
    setIsStreamingSession(withStreaming);
    isMaxFPSSessionRef.current = withMaxFPS;
    setIsMaxFPSSession(withMaxFPS);
    setShowStartDropdown(false);
    if (showPreStartChecklist) {
      setCameraCheckboxChecked(false);
      setBeepCheckboxChecked(false);
      setShowPreStartDialog(true);
    } else {
      handleStartExercise();
    }
  };

  // Handle start with streaming
  const handleStartWithStreaming = () => {
    handleStartClick({ withStreaming: true });
  };

  // Handle start with max FPS (for benchmarking)
  const handleStartWithMaxFPS = () => {
    handleStartClick({ withMaxFPS: true });
  };

  // Handle start exercise
  const handleStartExercise = () => {
    setShowPreStartDialog(false);
    setIsStopped(false);

    // Reset detector state for a new camera session
    resetCounter();
    initAudioContext();
    setSaveMessage(null);

    // lastRepCountRef mirrors the displayed rep count (initialReps + new reps).
    // The strategy's detection loop owns its own internal counter for delta
    // detection — this ref is read by drawFrame to render the count on canvas.
    // lastSavedReps tracks the displayed rep count for checkpoint saving.
    lastRepCountRef.current = repCount;
    lastSavedReps.current = repCount;

    // Only reset displayed rep count if starting completely fresh (no saved progress)
    if (initialReps === 0) {
      setRepCount(0);
    }
    // If we have saved progress (initialReps > 0), preserve the current count
    // New reps will be added to repCount via handleRepDetected

    handleStartCamera();
  };

  // Handle finish
  const handleFinish = () => {
    setIsCompleted(true);
    if (onComplete) {
      onComplete({ completed: true, reps: repCount });
    }
  };

  // Handle sound test
  const handleSoundTest = () => {
    playTestBeep();
    setShowSoundTestDialog(true);
  };

  // Handle report inaccuracy
  const handleReportInaccuracy = async () => {
    if (!reportedReps || isNaN(parseInt(reportedReps))) {
      alert('Please enter the actual number of reps you performed');
      return;
    }

    setIsUploading(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      // Use video dimensions captured at session start for correct coordinate normalization
      const videoDimensions = recordedVideoDimensionsRef.current || { width: 640, height: 480 };

      const response = await fetch('/api/physical_accuracy_reports', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          reported_reps: parseInt(reportedReps),
          tracked_reps: repCount,
          movement_log: movementLog,
          exercise_type: exerciseType,
          video_dimensions: videoDimensions
        })
      });

      if (!response.ok) {
        throw new Error(`Report failed: ${response.statusText}`);
      }

      const result = await response.json();
      alert(result.message || 'Report submitted successfully!');
      setShowReportDialog(false);
      setReportedReps('');
    } catch (err) {
      console.error('Failed to submit report:', err);
      alert(`Failed to submit report: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle report inaccurate tracking - saves recording for ML training review
  const handleReportInaccurate = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      // Auto-generate filename: auto-train-YYYY-mm-DD-HH-mm
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const autoName = `auto-train-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}-${pad(now.getMinutes())}`;

      const recordedFrames = strategy.sliceMovementLog(movementLogRef.current, frameCounterRef.current);

      // Use video dimensions captured at session start for correct coordinate normalization
      // This is critical because keypoints are in video coordinate space
      const videoDimensions = recordedVideoDimensionsRef.current || { width: 640, height: 480 };

      const data = {
        name: autoName,
        timestamp: now.toISOString(),
        totalFrames: recordedFrames.length,
        duration: recordedFrames.length > 0 ? recordedFrames[recordedFrames.length - 1].timestamp : 0,
        strokeCount: repCount,
        videoDimensions,
        frames: recordedFrames,
        reportedInaccurate: true,
        exerciseType
      };

      const response = await fetch('/api/save-pickleball-movement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      const result = await response.json();
      if (result.success) {
        setSaveMessage({ type: 'success', text: `Reported! Saved as ${result.filename}` });
      } else {
        setSaveMessage({ type: 'error', text: result.error || 'Failed to save' });
      }
    } catch (err) {
      setSaveMessage({ type: 'error', text: `Error: ${err.message}` });
    } finally {
      setIsSaving(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupDetectionRef.current) {
        cleanupDetectionRef.current();
      }
      stopCamera();
      cleanupAudio();
      if (streamingUploadUrl) {
        cleanupRecording();
      }
    };
  }, [streamingUploadUrl]);

  // Render loading state
  if (isLoading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>{l.loading_model}</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return <div className="alert alert-danger" role="alert">{error}</div>;
  }

  const progress = Math.min(100, (repCount / requiredReps) * 100);
  // Always mirror the video/canvas for a mirror-like selfie experience
  const transformStyle = 'scaleX(-1)';

  // Render component
  return (
    <div className="exercise-counter">
      <div className="card">
        <div className="card-body">
          <h2 className="card-title text-center mb-4">{l.title}</h2>

          <div className="position-relative mb-3" style={{ maxWidth: '640px', margin: '0 auto' }}>
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: 'auto',
                transform: transformStyle,
                display: isActive ? 'block' : 'none'
              }}
              playsInline
            />
            <canvas
              ref={canvasRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                transform: transformStyle,
                pointerEvents: 'none',
                display: isActive ? 'block' : 'none'
              }}
            />
            {!isActive && (
              <div className="text-center bg-light rounded" style={{ padding: '20px' }}>
                {cameraLoading ? (
                  <div className="spinner-border text-primary" role="status" style={{ marginTop: '60px', marginBottom: '60px' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <img
                    src={
                      exerciseType === 'pickleball'
                        ? '/images/physical/kid-pickleball.png'
                        : exerciseType === 'soccer'
                          ? '/images/physical/kid-soccer.png'
                          : '/images/physical/kid-skipping.png'
                    }
                    alt="Exercise illustration"
                    style={{ maxWidth: '100%', height: 'auto' }}
                    data-testid="kid-physical-illustration"
                  />
                )}
              </div>
            )}
            {/* Model versions - displayed under canvas */}
            {debugState.modelVersions && strategy.getModelVersionsDisplay && (
              <div style={{ marginTop: '4px', fontSize: '11px', color: '#999', fontFamily: 'monospace', textAlign: 'center' }}>
                {strategy.getModelVersionsDisplay(debugState.modelVersions)}
              </div>
            )}
          </div>

          <div
            className="text-center mb-4"
            data-testid="physical-activity-interface"
            data-inference-engine={currentEngine || ""}
          >
            <div className="display-3 fw-bold text-primary" data-testid="reps-counter">
              {repCount}
            </div>
            <div className="text-muted">
              {l.reps_completed} {requiredReps > 0 && `(${l.goal}: ${requiredReps})`}
            </div>
            {isSavingProgress && (
              <div className="text-info small mt-1" data-testid="saving-progress-indicator">
                Saving...
              </div>
            )}
            {requiredReps > 0 && (
              <div className="progress mt-2" style={{ height: '10px' }} data-testid="goal-progress-bar">
                <div
                  className="progress-bar bg-success"
                  role="progressbar"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
            {/* Video upload status */}
            {streamingUploadUrl && videoUploadStatus && (
              <div className={`alert ${videoUploadStatus === 'success' ? 'alert-success' : 'alert-danger'} py-1 px-2 mt-2 mb-0`} style={{ fontSize: '12px' }}>
                {videoUploadMessage}
              </div>
            )}
            {streamingUploadUrl && isUploadingVideo && (
              <div className="text-info small mt-2 d-flex align-items-center justify-content-center">
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                {videoUploadMessage || 'Uploading recording...'}
              </div>
            )}
          </div>

          <div className="d-grid gap-2" style={{ overflow: 'visible' }}>
            {!isStopped ? (
              <div className="d-flex gap-2" style={{ overflow: 'visible' }}>
                {isActive ? (
                  <button
                    className="btn btn-lg flex-grow-1 btn-danger"
                    onClick={handleStop}
                    disabled={isCompleted}
                    data-testid="stop-exercise-button"
                  >
                    {l.stop_exercise}
                    {isStreamingSession && isVideoRecording && (
                      <span className="badge bg-light text-danger ms-2">
                        🔴 {Math.floor(recordingDuration / 60)}:{String(recordingDuration % 60).padStart(2, '0')} ({uploadedChunks})
                      </span>
                    )}
                  </button>
                ) : (streamingUploadUrl || showMaxFPSOption) ? (
                  /* Dropdown button for debugging options */
                  <div className="btn-group flex-grow-1" style={{ position: 'relative', overflow: 'visible' }}>
                    <button
                      className="btn btn-lg btn-primary"
                      onClick={() => handleStartClick()}
                      disabled={isCompleted}
                      data-testid="start-exercise-button"
                    >
                      {l.start_exercise}
                    </button>
                    <button
                      type="button"
                      className="btn btn-lg btn-primary dropdown-toggle dropdown-toggle-split"
                      style={{ maxWidth: '60px' }}
                      onClick={() => setShowStartDropdown(!showStartDropdown)}
                      disabled={isCompleted}
                      data-testid="start-dropdown-toggle"
                    >
                      <span className="visually-hidden">Toggle Dropdown</span>
                    </button>
                    {showStartDropdown && (
                      <ul className="dropdown-menu show" style={{ position: 'absolute', inset: 'auto', top: '100%', right: 0, minWidth: '100%' }}>
                        <li>
                          <button
                            className="dropdown-item"
                            onClick={() => handleStartClick()}
                            data-testid="start-normal-option"
                          >
                            {l.start_exercise}
                          </button>
                        </li>
                        {streamingUploadUrl && (
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={handleStartWithStreaming}
                              data-testid="start-streaming-option"
                            >
                              🔴 {l.start_with_streaming}
                            </button>
                          </li>
                        )}
                        {showMaxFPSOption && (
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={handleStartWithMaxFPS}
                              data-testid="start-maxfps-option"
                            >
                              ⚡ Start with Max FPS
                            </button>
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                ) : (
                  <button
                    className="btn btn-lg flex-grow-1 btn-primary"
                    onClick={() => handleStartClick()}
                    disabled={isCompleted}
                    data-testid="start-exercise-button"
                  >
                    {l.start_exercise}
                  </button>
                )}
                <button
                  className="btn btn-lg btn-outline-secondary"
                  onClick={handleSoundTest}
                  title={l.test_sound}
                  data-testid="test-sound-button"
                >
                  {l.test_sound}
                </button>
              </div>
            ) : (
              <div className="d-grid gap-2">
                {repCount >= requiredReps && (
                  <button
                    className="btn btn-lg btn-success"
                    onClick={handleFinish}
                    disabled={isCompleted}
                    data-testid="finish-exercise-button"
                  >
                    {l.finish_exercise} ({repCount} reps)
                  </button>
                )}
                {repCount < requiredReps && (
                  (streamingUploadUrl || showMaxFPSOption) ? (
                    /* Dropdown button for debugging options */
                    <div className="btn-group w-100" style={{ position: 'relative' }}>
                      <button
                        className="btn btn-lg btn-primary"
                        onClick={() => handleResumeClick()}
                        disabled={isCompleted}
                        data-testid="resume-exercise-button"
                      >
                        {l.resume_exercise} ({repCount}/{requiredReps} reps)
                      </button>
                      <button
                        type="button"
                        className="btn btn-lg btn-primary dropdown-toggle dropdown-toggle-split"
                        style={{ maxWidth: '60px' }}
                        onClick={() => setShowResumeDropdown(!showResumeDropdown)}
                        disabled={isCompleted}
                        data-testid="resume-dropdown-toggle"
                      >
                        <span className="visually-hidden">Toggle Dropdown</span>
                      </button>
                      {showResumeDropdown && (
                        <ul className="dropdown-menu show" style={{ position: 'absolute', inset: 'auto', top: '100%', right: 0, minWidth: '100%' }}>
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => handleResumeClick()}
                              data-testid="resume-normal-option"
                            >
                              {l.resume_exercise}
                            </button>
                          </li>
                          {streamingUploadUrl && (
                            <li>
                              <button
                                className="dropdown-item"
                                onClick={() => handleResumeClick({ withStreaming: true })}
                                data-testid="resume-streaming-option"
                              >
                                🔴 {l.resume_with_streaming}
                              </button>
                            </li>
                          )}
                          {showMaxFPSOption && (
                            <li>
                              <button
                                className="dropdown-item"
                                onClick={() => handleResumeClick({ withMaxFPS: true })}
                                data-testid="resume-maxfps-option"
                              >
                                ⚡ Resume with Max FPS
                              </button>
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <button
                      className="btn btn-lg btn-primary"
                      onClick={handleResume}
                      disabled={isCompleted}
                      data-testid="resume-exercise-button"
                    >
                      {l.resume_exercise} ({repCount}/{requiredReps} reps)
                    </button>
                  )
                )}
                {showReportButton && (
                  <button
                    className="btn btn-lg btn-outline-warning"
                    onClick={() => setShowReportDialog(true)}
                    disabled={isCompleted || movementLog.length === 0}
                    data-testid="report-inaccuracy-button"
                  >
                    {l.report_inaccuracy}
                  </button>
                )}
              </div>
            )}
            {railsEnv !== 'production' && (
              <button
                className="btn btn-warning"
                onClick={() => {
                  handleRepDetected(10);
                  movementLogRef.current.push({ timestamp: Date.now(), fake: true, reps: 10 });
                  setMovementLog([...movementLogRef.current]);
                }}
                disabled={isCompleted}
                data-testid="fake-reps-button"
              >
                Fake 10 Reps (Test Only)
              </button>
            )}
          </div>


          {/* Sound Test Dialog */}
          {showSoundTestDialog && (
            <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} data-testid="sound-test-dialog">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-body text-center p-4">
                    <h5 className="mb-4">{l.did_you_hear_beep}</h5>
                    <div className="d-flex gap-3 justify-content-center">
                      <button className="btn btn-success btn-lg px-4" onClick={() => setShowSoundTestDialog(false)} data-testid="sound-test-yes">
                        {l.yes}
                      </button>
                      <button className="btn btn-danger btn-lg px-4" onClick={() => { setShowSoundTestDialog(false); setShowSilentModeWarning(true); }} data-testid="sound-test-no">
                        {l.no}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Silent Mode Warning Dialog */}
          {showSilentModeWarning && (
            <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} data-testid="silent-mode-warning">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-body text-center p-4">
                    <p className="mb-4">{l.check_silent_mode}</p>
                    <button className="btn btn-primary btn-lg" onClick={() => setShowSilentModeWarning(false)} data-testid="silent-mode-ok">
                      {l.ok}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pre-Start Checklist Dialog */}
          {showPreStartDialog && (
            <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} data-testid="pre-start-checklist">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-body p-4">
                    <div className="form-check mb-4 d-flex align-items-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="cameraCheckbox"
                        checked={cameraCheckboxChecked}
                        onChange={(e) => setCameraCheckboxChecked(e.target.checked)}
                        data-testid="camera-checkbox"
                        style={{ width: '32px', height: '32px', cursor: 'pointer', flexShrink: 0, border: '3px solid #0d6efd', borderRadius: '6px' }}
                      />
                      <label className="form-check-label ms-3" htmlFor="cameraCheckbox" style={{ fontSize: '1.1rem', fontWeight: '600', cursor: 'pointer', lineHeight: '1.4' }}>
                        {l.camera_sees_whole_body}
                      </label>
                    </div>
                    <div className="form-check mb-4 d-flex align-items-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="beepCheckbox"
                        checked={beepCheckboxChecked}
                        onChange={(e) => setBeepCheckboxChecked(e.target.checked)}
                        data-testid="beep-checkbox"
                        style={{ width: '32px', height: '32px', cursor: 'pointer', flexShrink: 0, border: '3px solid #0d6efd', borderRadius: '6px' }}
                      />
                      <label className="form-check-label ms-3" htmlFor="beepCheckbox" style={{ fontSize: '1.1rem', fontWeight: '600', cursor: 'pointer', lineHeight: '1.4' }}>
                        {l.beep_on_correct_rep}
                      </label>
                    </div>
                    <div className="text-center">
                      <button
                        className="btn btn-primary btn-lg"
                        onClick={handleStartExercise}
                        disabled={!cameraCheckboxChecked || !beepCheckboxChecked}
                        data-testid="checklist-start-button"
                      >
                        {l.start}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Report Inaccuracy Dialog */}
          {showReportDialog && (
            <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Report Inaccurate Tracking</h5>
                    <button type="button" className="btn-close" onClick={() => setShowReportDialog(false)} />
                  </div>
                  <div className="modal-body">
                    <p>The system tracked <strong>{repCount}</strong> reps.</p>
                    <p>How many reps did you actually perform?</p>
                    <input
                      type="number"
                      className="form-control"
                      value={reportedReps}
                      onChange={(e) => setReportedReps(e.target.value)}
                      placeholder="Enter actual reps"
                      min="0"
                      data-testid="reported-reps-input"
                    />
                    <small className="text-muted">Your feedback helps improve the tracking accuracy.</small>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowReportDialog(false)} disabled={isUploading}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleReportInaccuracy} disabled={isUploading} data-testid="submit-report-button">
                      {isUploading ? 'Submitting...' : 'Submit Report'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnifiedExerciseCounter;
